import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import type { SessionUser } from 'models/auth/SessionUser';
import AppShell, { NavItem } from './AppShell';
import { clearAccessToken } from 'models/auth/accessTokenStore';

type Props = {
  children: React.ReactNode;
};

const AuthenticatedShell: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionUser = await ApiService.getMe();
        setUser(sessionUser);
      } catch (e) {
        console.error('Failed to bootstrap session', e);
        setError('Failed to load session');
        clearAccessToken();
        localStorage.removeItem('userId');
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    void bootstrap();
  }, [navigate, location.pathname]);

  const homeHref = useMemo(() => {
    return '/home';
    //return user.isArtist && user.artistId ? `/artist/${user.artistId}` : '/home';
  }, [user]);

  const navItems = useMemo<NavItem[]>(() => {
    if (!user) return [];

    const homePath = user.isArtist && user.artistId ? `/artist/${user.artistId}` : '/home';
    const items: NavItem[] = [{ label: 'Home', to: homePath }];

    if (user.isArtist && user.artistId) {
      items.push({
        label: 'Blue Prints',
        children: [
          { label: 'Workflows', to: `/artist/${user.artistId}/team/workflows` },
          { label: 'Forms', to: `/artist/${user.artistId}/team/forms` },
          { label: 'Team', to: `/artist/${user.artistId}/team/` },
        ],
      });
      items.push({ label: 'Operations', to: `/artist/${user.artistId}/operations` });
    }

    return items;
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading...
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error || 'Unauthorized'}
      </div>
    );
  }

  return (
    <AppShell user={user} homeHref={homeHref} navItems={navItems}>
      {children}
    </AppShell>
  );
};

export default AuthenticatedShell;
